import React, { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Menu, X, ChevronDown, Phone, ShieldAlert, Wrench, MessageSquare } from "lucide-react";
import { servicesData } from "@/mock";

export default function Navbar() {
  const [isOpen, setIsOpen] = useState(false);
  const [isServicesDropdownOpen, setIsServicesDropdownOpen] = useState(false);
  const [showPhoneDropdown, setShowPhoneDropdown] = useState(false);
  const [showMobilePhoneDropdown, setShowMobilePhoneDropdown] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const handleServiceClick = (serviceId) => {
    setIsServicesDropdownOpen(false);
    setIsOpen(false);
    navigate(`/services/${serviceId}`);
  };

  const isActive = (path) => {
    return location.pathname === path;
  };

  return (
    <nav className="sticky top-0 z-50 bg-zinc-950/95 backdrop-blur-md border-b border-zinc-900">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          {/* Logo Brand */}
          <div className="flex-shrink-0 flex items-center">
            <Link to="/" className="flex items-center gap-2 group">
              <div className="p-2 bg-blue-600 rounded-lg group-hover:bg-blue-500 transition-colors">
                <Wrench className="h-6 w-6 text-white" />
              </div>
              <div>
                <span className="font-extrabold text-2xl tracking-wider text-white">
                  KG <span className="text-blue-500">AUTO</span>
                </span>
                <p className="text-[10px] tracking-widest text-zinc-400 font-bold uppercase">REPAIR</p>
              </div>
            </Link>
          </div>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-8">
            <Link
              to="/"
              className={`text-sm font-semibold transition-colors duration-200 ${
                isActive("/") ? "text-blue-500" : "text-zinc-300 hover:text-white"
              }`}
            >
              Home
            </Link>

            {/* Services Dropdown */}
            <div className="relative">
              <button
                onClick={() => setIsServicesDropdownOpen(!isServicesDropdownOpen)}
                onMouseEnter={() => setIsServicesDropdownOpen(true)}
                className="flex items-center gap-1 text-sm font-semibold text-zinc-300 hover:text-white focus:outline-none py-2"
              >
                Services
                <ChevronDown className={`h-4 w-4 transform transition-transform duration-200 ${isServicesDropdownOpen ? "rotate-180" : ""}`} />
              </button>

              {isServicesDropdownOpen && (
                <div
                  className="absolute left-0 mt-2 w-72 rounded-xl bg-zinc-900 border border-zinc-800 shadow-2xl p-2 z-50"
                  onMouseLeave={() => setIsServicesDropdownOpen(false)}
                >
                  <div className="grid grid-cols-1 gap-1">
                    {servicesData.map((service) => (
                      <button
                        key={service.id}
                        onClick={() => handleServiceClick(service.id)}
                        className="flex flex-col text-left p-3 rounded-lg hover:bg-zinc-800 transition-colors duration-150 group"
                      >
                        <span className="text-sm font-semibold text-zinc-100 group-hover:text-blue-400">
                          {service.title}
                        </span>
                        <span className="text-xs text-zinc-400 line-clamp-1 mt-0.5">
                          {service.subtitle}
                        </span>
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <a
              href="#about"
              className="text-sm font-semibold text-zinc-300 hover:text-white transition-colors duration-200"
              onClick={(e) => {
                if (location.pathname !== "/") {
                  e.preventDefault();
                  navigate("/#about");
                }
              }}
            >
              Why KG Auto
            </a>

            <a
              href="#contact"
              className="text-sm font-semibold text-zinc-300 hover:text-white transition-colors duration-200"
              onClick={(e) => {
                if (location.pathname !== "/") {
                  e.preventDefault();
                  navigate("/#contact");
                }
              }}
            >
              Contact
            </a>
          </div>

          {/* Call to Action Button with Dropdown (Call / Text options) */}
          <div className="hidden md:flex items-center gap-4 relative">
            <button
              onClick={() => setShowPhoneDropdown(!showPhoneDropdown)}
              className="flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-bold bg-blue-600 hover:bg-blue-500 text-white transition-colors shadow-lg shadow-blue-900/20 focus:outline-none"
            >
              <Phone className="h-4 w-4" />
              <span>304-685-3724</span>
              <ChevronDown className={`h-4 w-4 transform transition-transform duration-150 ${showPhoneDropdown ? "rotate-180" : ""}`} />
            </button>

            {showPhoneDropdown && (
              <div className="absolute right-0 top-12 mt-2 w-52 rounded-xl bg-zinc-900 border border-zinc-800 shadow-2xl p-1.5 z-50 animate-fade-in flex flex-col">
                <a
                  href="tel:3046853724"
                  onClick={() => setShowPhoneDropdown(false)}
                  className="flex items-center gap-2.5 p-3 rounded-lg text-xs font-bold text-zinc-100 hover:bg-zinc-800 hover:text-white transition-colors"
                >
                  <Phone className="h-4 w-4 text-blue-500" />
                  <span>Call 304-685-3724</span>
                </a>
                <a
                  href="sms:3046853724"
                  onClick={() => setShowPhoneDropdown(false)}
                  className="flex items-center gap-2.5 p-3 rounded-lg text-xs font-bold text-zinc-100 hover:bg-zinc-800 hover:text-white transition-colors border-t border-zinc-850"
                >
                  <MessageSquare className="h-4 w-4 text-blue-500" />
                  <span>Text 304-685-3724</span>
                </a>
              </div>
            )}
          </div>

          {/* Mobile menu button */}
          <div className="md:hidden flex items-center">
            <button
              onClick={() => setIsOpen(!isOpen)}
              className="text-zinc-400 hover:text-white p-2 focus:outline-none"
              aria-label="Toggle Menu"
            >
              {isOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      {isOpen && (
        <div className="md:hidden bg-zinc-950 border-b border-zinc-900 px-4 pt-2 pb-6 space-y-4">
          <div className="flex flex-col space-y-2">
            <Link
              to="/"
              onClick={() => setIsOpen(false)}
              className={`p-2 rounded-lg text-base font-medium transition-colors ${
                isActive("/") ? "bg-blue-950/30 text-blue-400" : "text-zinc-300 hover:bg-zinc-900 hover:text-white"
              }`}
            >
              Home
            </Link>

            <div className="p-2">
              <span className="text-xs font-bold text-zinc-500 uppercase tracking-wider block px-2 pb-2 border-b border-zinc-900">
                Services
              </span>
              <div className="mt-2 pl-2 space-y-1">
                {servicesData.map((service) => (
                  <button
                    key={service.id}
                    onClick={() => handleServiceClick(service.id)}
                    className="flex flex-col text-left w-full py-2 px-3 rounded-lg hover:bg-zinc-900 transition-colors group"
                  >
                    <span className="text-sm font-semibold text-zinc-100 group-hover:text-blue-400">
                      {service.title}
                    </span>
                  </button>
                ))}
              </div>
            </div>

            <a
              href="#about"
              onClick={() => {
                setIsOpen(false);
                if (location.pathname !== "/") {
                  navigate("/#about");
                }
              }}
              className="p-2 rounded-lg text-base font-medium text-zinc-300 hover:bg-zinc-900 hover:text-white transition-colors"
            >
              Why KG Auto
            </a>

            <a
              href="#contact"
              onClick={() => {
                setIsOpen(false);
                if (location.pathname !== "/") {
                  navigate("/#contact");
                }
              }}
              className="p-2 rounded-lg text-base font-medium text-zinc-300 hover:bg-zinc-900 hover:text-white transition-colors"
            >
              Contact
            </a>
          </div>

          <div className="pt-4 border-t border-zinc-900 flex flex-col sm:flex-row gap-3">
            <a
              href="tel:3046853724"
              onClick={() => setIsOpen(false)}
              className="flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-lg text-base font-bold bg-blue-600 hover:bg-blue-500 text-white transition-colors text-center"
            >
              <Phone className="h-5 w-5" />
              <span>Call 304-685-3724</span>
            </a>
            <a
              href="sms:3046853724"
              onClick={() => setIsOpen(false)}
              className="flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-lg text-base font-bold bg-zinc-900 border border-zinc-800 hover:bg-zinc-850 text-zinc-200 transition-colors text-center"
            >
              <MessageSquare className="h-5 w-5 text-blue-500" />
              <span>Text 304-685-3724</span>
            </a>
          </div>
        </div>
      )}
    </nav>
  );
}
